/**
 * GL Wishlist — lightweight, localStorage-based wishlist for product cards.
 * - Persists across sessions using localStorage.
 * - Only runs click handlers when the document is visible (prevents background tab issues).
 * - Heart fills red on toggle.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'gl_wishlist_items';

  let memoryStorage = null;

  /* ── helpers ───────────────────────────────────────── */
  function getWishlist() {
    if (memoryStorage !== null) return memoryStorage;
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function saveWishlist(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      memoryStorage = null; // Successfully saved to localStorage
    } catch (e) {
      // localStorage is blocked (e.g. inside Shopify Theme Editor iframe)
      memoryStorage = list; // Fallback to in-memory array so it still works visually
    }
  }

  function isWishlisted(productId) {
    return getWishlist().indexOf(String(productId)) !== -1;
  }

  function toggleWishlist(productId) {
    const id = String(productId);
    let list = getWishlist();
    const idx = list.indexOf(id);
    if (idx > -1) {
      list.splice(idx, 1);
    } else {
      list.push(id);
    }
    saveWishlist(list);
    return idx === -1; // true = now wishlisted
  }

  /* ── render initial state ─────────────────────────── */
  function syncButtons() {
    document.querySelectorAll('.gl-wishlist-btn').forEach(function (btn) {
      const pid = btn.getAttribute('data-product-id');
      if (isWishlisted(pid)) {
        btn.classList.add('wishlisted');
      } else {
        btn.classList.remove('wishlisted');
      }
    });
  }

  /* ── event delegation (single listener on body) ───── */
  function handleClick(e) {
    // Removed document.hidden check as it can falsely trigger inside the Shopify Editor iframe

    const btn = e.target.closest('.gl-wishlist-btn');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const pid = btn.getAttribute('data-product-id');
    if (!pid) return;

    const nowWishlisted = toggleWishlist(pid);

    // Update ALL buttons for this product (may appear in multiple sections)
    document.querySelectorAll('.gl-wishlist-btn[data-product-id="' + pid + '"]').forEach(function (b) {
      if (nowWishlisted) {
        b.classList.add('wishlisted');
        // Re-trigger the pop animation
        b.style.animation = 'none';
        // Force reflow
        void b.offsetWidth;
        b.style.animation = '';
      } else {
        b.classList.remove('wishlisted');
      }
    });
  }

  /* ── init ──────────────────────────────────────────── */
  function init() {
    syncButtons();
    
    // Bind to document using capture phase (true) to bypass any stopPropagation() from other scripts!
    document.addEventListener('click', handleClick, true);

    // Re-sync when a tab becomes visible again (multi-tab support)
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) syncButtons();
    });

    // Observe DOM mutations to sync buttons loaded via AJAX (e.g. Product Recommendations)
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(function (mutations) {
        let shouldSync = false;
        for (let i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes.length > 0) {
            shouldSync = true;
            break;
          }
        }
        if (shouldSync) {
          syncButtons();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
